import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import BlogEditor from '@/components/BlogEditor';
import { Loader2 } from 'lucide-react';

export default function BlogManagementModal({ isOpen, onClose, blog, onSuccess }) {
  const isEditMode = !!blog;
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: '',
    excerpt: '',
    image: '',
    readTime: '',
    content: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        author: blog.author || '',
        category: blog.category || '',
        excerpt: blog.excerpt || '',
        image: blog.image || '',
        readTime: blog.readTime || '',
        content: blog.content || ''
      });
    } else {
      setFormData({
        title: '',
        author: '',
        category: '',
        excerpt: '',
        image: '',
        readTime: '',
        content: ''
      });
    }
  }, [blog]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContentChange = (html) => {
    setFormData({ ...formData, content: html });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode) {
        await api.updateBlog(blog.id, formData);
        toast.success('Blog post updated successfully!');
      } else {
        await api.createBlog(formData);
        toast.success('Blog post created successfully!');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || `Failed to ${isEditMode ? 'update' : 'create'} blog post`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="blog-management-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" data-testid="blog-modal-title">
            {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter blog title"
                required
                data-testid="blog-title-input"
              />
            </div>
            <div>
              <Label htmlFor="author">Author</Label>
              <Input
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
                data-testid="blog-author-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Technology, Leadership"
                data-testid="blog-category-input"
              />
            </div>
            <div>
              <Label htmlFor="readTime">Read Time</Label>
              <Input
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                placeholder="e.g., 5 min read"
                data-testid="blog-readtime-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="image">Image URL</Label>
            <Input
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              data-testid="blog-image-input"
            />
          </div>

          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Input
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Brief summary of the blog post"
              data-testid="blog-excerpt-input"
            />
          </div>

          <div>
            <Label>Content *</Label>
            <BlogEditor content={formData.content} onChange={handleContentChange} />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="blog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="blog-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Blog' : 'Create Blog'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
